import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { BarcodeScannerDialog } from "./BarcodeScannerDialog";

import { Tabs, TabsList, TabsTrigger } from "@/shared/components/atoms/tabs";

interface CodeScannerTabbedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScanResult?: (value: string) => void;
  defaultTab?: "qr" | "barcode";
  readerId?: string;
}

export const CodeScannerTabbedDialog = ({
  open,
  onOpenChange,
  onScanResult,
  defaultTab = "qr",
  readerId,
}: CodeScannerTabbedDialogProps) => {
  const { t } = useTranslation("common");
  const [activeTab, setActiveTab] = useState<"qr" | "barcode">(defaultTab);

  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]);

  return (
    <BarcodeScannerDialog
      open={open}
      onOpenChange={onOpenChange}
      onScanResult={onScanResult}
      mode={activeTab === "qr" ? "qr-only" : "code128-only"}
      title={
        activeTab === "qr"
          ? t("scanner.titleQr")
          : t("scanner.titleBarcode")
      }
      instruction={
        activeTab === "qr"
          ? t("scanner.instructionQrTab")
          : t("scanner.instructionBarcodeTab")
      }
      readerId={readerId ?? "scanner-tabbed"}
      headerAccessory={
        <div className="w-full max-w-full">
          <div className="rounded-xl border bg-muted/50 p-1.5 sm:p-2">
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as "qr" | "barcode")}
            >
              <TabsList className="!grid h-auto min-h-11 w-full grid-cols-2 gap-0.5 p-0.5 sm:min-h-10 sm:gap-0">
                <TabsTrigger
                  value="qr"
                  className="min-h-11 touch-manipulation px-2 text-xs sm:min-h-10 sm:px-3 sm:text-sm"
                >
                  <span className="sm:hidden">{t("scanner.tabQrShort")}</span>
                  <span className="hidden sm:inline">{t("scanner.tabQrLong")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="barcode"
                  className="min-h-11 touch-manipulation px-2 text-xs sm:min-h-10 sm:px-3 sm:text-sm"
                >
                  {t("scanner.tabBarcode")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      }
    />
  );
};
